import { AdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import { IconInfo24 } from '@warp-ds/icons/react';
import { Box } from '@warp-ds/react/components/box';
import { Pagination } from '@warp-ds/react/components/pagination';
import { type ReactNode, useEffect, useMemo } from 'react';
import { ApiActionStatus, PRICE_MIGHT_VARY_WARNING } from '@/interfaces';
import { trackResultPage } from '@/lib/utils/tracking';
import { EmptyWarning } from '../emptyWarning/EmptyWarning';
import { ResultListItem } from './components/ResultListItem';
import { useResultsNavigator } from './hooks/useResultsNavigator';

interface ResultListProps {
    paxEnabled?: boolean;
}

export const ResultList = ({ paxEnabled }: ResultListProps) => {
    const { results, filters, currentPage, totalPages, createHref, handleChange, resetFilters, resetAndRefetch } = useResultsNavigator();
    const shouldShowPriceWarning = filters.agesOfChildren?.some((age) => age > 12 || age < 2);
    const [bannerPositions, bannerElements] = AdPositionElements.use({ layoutType: 'list' });

    const items = useMemo(
        () => results.offers.map((o) => <ResultListItem key={o.offerId} offer={o} filters={filters} paxEnabled={paxEnabled} />),
        [results.offers, filters, paxEnabled],
    );

    const itemsWithBanners = useMemo(
        () =>
            insertBanners({
                adItems: items,
                bannerPositions: bannerPositions as number[],
                bannerElements: bannerElements as ReactNode[],
            }),
        [items, bannerPositions, bannerElements],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger new tracking when the list of offers changes
    useEffect(() => {
        trackResultPage(results, filters);
    }, [results.offers]); // Only trigger new tracking when the list of offers changes

    return (
        <section
            data-testid="resultList"
            className="flex flex-col gap-16"
            aria-busy={results.status === ApiActionStatus.PENDING || undefined}>
            <h2 className="sr-only">Resultater</h2>
            {results.offers.length === 0 ? (
                <EmptyWarning id="result" resetFilters={resetFilters} triggerFetch={resetAndRefetch} />
            ) : (
                <>
                    {shouldShowPriceWarning && paxEnabled && (
                        <Box info>
                            <div className="flex gap-x-16">
                                <IconInfo24 className="s-text-link mt-2" />
                                <p className="mb-0">{PRICE_MIGHT_VARY_WARNING}</p>
                            </div>
                        </Box>
                    )}
                    {itemsWithBanners}
                    {totalPages > 1 && (
                        <Pagination createHref={createHref} currentPage={currentPage} lastPage={totalPages} onChange={handleChange} />
                    )}
                </>
            )}
        </section>
    );
};
